import React, {createContext, useContext, useRef} from 'react'
import {Config, Region, LivePreview, Stack} from 'contentstack'
// import ContentstackLivePreview from '@contentstack/live-preview-utils'

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const ContentstackContext = createContext<ContentstackContextType | null>(null)

interface ContentstackContextType {
    stack: Stack
}

// api host url
export const customHostUrl = (baseUrl = ''): string => {
    return baseUrl.replace('api', 'cdn')
}
// generate prod api urls
export const generateUrlBasedOnRegion = (): string[] => {
    return Object.keys(Region).map((region) => {
        if (region === 'US') {
            return `cdn.contentstack.io`
        }
        return `${region}-cdn.contentstack.com`
    })
}
// prod url validation for custom host
export const isValidCustomHostUrl = (url: string): boolean => {
    return url ? !generateUrlBasedOnRegion().includes(url) : false
}

export const initializeStack = () => {
    const config = getConfig()
    const {
        customParams: {contentstack},
    } = config

    const {
        REACT_APP_CONTENTSTACK_API_KEY,
        REACT_APP_CONTENTSTACK_DELIVERY_TOKEN,
        REACT_APP_CONTENTSTACK_ENVIRONMENT,
        REACT_APP_CONTENTSTACK_BRANCH,
        REACT_APP_CONTENTSTACK_REGION,
        REACT_APP_CONTENTSTACK_PREVIEW_TOKEN,
        REACT_APP_CONTENTSTACK_APP_HOST,
        REACT_APP_CONTENTSTACK_LIVE_PREVIEW,
        REACT_APP_CONTENTSTACK_PREVIEW_HOST,
        REACT_APP_CONTENTSTACK_API_HOST,
    } = contentstack

    const isBasicConfigValid = () => {
        return (
            !!REACT_APP_CONTENTSTACK_API_KEY &&
            !!REACT_APP_CONTENTSTACK_DELIVERY_TOKEN &&
            !!REACT_APP_CONTENTSTACK_ENVIRONMENT
        )
    }

    const isLpConfigValid = () => {
        return (
            !!REACT_APP_CONTENTSTACK_LIVE_PREVIEW &&
            !!REACT_APP_CONTENTSTACK_PREVIEW_TOKEN &&
            !!REACT_APP_CONTENTSTACK_PREVIEW_HOST &&
            !!REACT_APP_CONTENTSTACK_APP_HOST
        )
    }
    // set region
    const setRegion = (): Region => {
        let region = 'US' as keyof typeof Region
        if (!!REACT_APP_CONTENTSTACK_REGION && REACT_APP_CONTENTSTACK_REGION !== 'us') {
            region = REACT_APP_CONTENTSTACK_REGION.toLocaleUpperCase().replace(
                '-',
                '_',
            ) as keyof typeof Region
        }
        return Region[region]
    }
    // set LivePreview config
    const setLivePreviewConfig = (): LivePreview => {
        if (!isLpConfigValid())
            throw new Error(
                'Your LP config is set to true. Please make you have set all required LP config in .env',
            )
        return {
            preview_token: REACT_APP_CONTENTSTACK_PREVIEW_TOKEN,
            enable: REACT_APP_CONTENTSTACK_LIVE_PREVIEW === 'true',
            host: REACT_APP_CONTENTSTACK_PREVIEW_HOST,
        } as LivePreview
    }

    const initializeContentStackSdk = (): Stack => {
        if (!isBasicConfigValid())
            throw new Error('Please set you .env file before running starter app')
        const stackConfig: Config = {
            api_key: REACT_APP_CONTENTSTACK_API_KEY,
            delivery_token: REACT_APP_CONTENTSTACK_DELIVERY_TOKEN,
            environment: REACT_APP_CONTENTSTACK_ENVIRONMENT,
            region: setRegion(),
            branch: REACT_APP_CONTENTSTACK_BRANCH || 'main',
        }
        if (REACT_APP_CONTENTSTACK_LIVE_PREVIEW === 'true') {
            stackConfig.live_preview = setLivePreviewConfig()
        }
        return Stack(stackConfig)
    }

    const stack = initializeContentStackSdk()

    const customHostBaseUrl = REACT_APP_CONTENTSTACK_API_HOST
        ? customHostUrl(REACT_APP_CONTENTSTACK_API_HOST)
        : ''

    if (customHostBaseUrl && isValidCustomHostUrl(customHostBaseUrl)) {
        stack.setHost(customHostBaseUrl)
    }

    // // Setting LP if enabled
    // ContentstackLivePreview.init({
    //     stackSdk: Stack,
    //     clientUrlParams: {
    //         host: REACT_APP_CONTENTSTACK_APP_HOST,
    //     },
    // })?.catch((error) => console.error(error))

    // const {onEntryChange} = ContentstackLivePreview

    return stack
}

// this provider is needed to initialize the stack because of a problem with getConfig and pwa kit
// github issue: https://github.com/SalesforceCommerceCloud/pwa-kit/issues/1400
export const ContentstackProvider = ({children}: {children: React.ReactNode}) => {
    const stackRef = useRef<Stack>(initializeStack())

    return (
        <ContentstackContext.Provider value={{stack: stackRef.current}}>
            {children}
        </ContentstackContext.Provider>
    )
}

export const useContentstack = () => {
    return useContext(ContentstackContext)
}

// export const useContentstackLivePreview = () => {
//     return useContext(ContentstackContext)
// }
